import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, ArrowRight, CheckCircle2, Circle, CircleDot, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { friendlyMessage } from '@/api/http'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useOnboardingProgress, type OnboardingStepProgress } from './useOnboardingProgress'

const DISMISSED_KEY = 'nf.onboarding.checklist.dismissed.v1'

function readDismissed() {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

function writeDismissed() {
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Onboarding remains usable when storage is unavailable.
  }
}

function StepIcon({ status }: { status: OnboardingStepProgress['status'] }) {
  if (status === 'done') return <CheckCircle2 className="size-5 text-emerald-600" />
  if (status === 'current') return <CircleDot className="size-5 text-primary" />
  return <Circle className="size-5 text-muted-foreground" />
}

function StepCard({ step }: { step: OnboardingStepProgress }) {
  const { t } = useTranslation()

  return (
    <div className="flex min-h-[156px] flex-col justify-between rounded-lg border bg-background p-4">
      <div className="flex items-start gap-3">
        <StepIcon status={step.status} />
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold tracking-normal">{t(`onboarding.steps.${step.id}.title`)}</h3>
            <Badge variant={step.status === 'current' ? 'default' : 'secondary'}>{t(`onboarding.status.${step.status}`)}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">{t(`onboarding.steps.${step.id}.description`)}</p>
        </div>
      </div>
      <Button asChild variant={step.status === 'current' ? 'default' : 'outline'} className="mt-4 w-fit">
        <Link to={step.route}>
          {t(`onboarding.steps.${step.id}.action`)}
          <ArrowRight className="size-4" />
        </Link>
      </Button>
    </div>
  )
}

export function OnboardingChecklist({ userId }: { userId?: string }) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(readDismissed)
  const { progress, isLoading, error, refetch } = useOnboardingProgress(userId)

  useEffect(() => {
    if (!progress.completed) return
    writeDismissed()
    setDismissed(true)
  }, [progress.completed])

  if (!userId || dismissed) return null

  return (
    <Card className="mb-6 rounded-lg">
      <CardHeader className="gap-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg tracking-normal">{t('onboarding.title')}</CardTitle>
            <CardDescription>{t('onboarding.description')}</CardDescription>
          </div>
          {error ? (
            <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="size-4" />
              {t('common.retry')}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            {friendlyMessage(error, t('onboarding.loadFailed'))}
          </div>
        ) : isLoading ? (
          <div className="grid gap-3 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-[156px] rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-3">
            {progress.steps.map((step) => (
              <StepCard key={step.id} step={step} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
